import { Component, computed, inject, input, signal } from '@angular/core';
import { EmployeeService, OperationService, trackByIndex } from '@beautyapp/core';
import { map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OperationDetailComponent } from '@beautyapp/operation';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import subMonths from 'date-fns/subMonths';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { NzDatePickerComponent, NzRangePickerComponent } from 'ng-zorro-antd/date-picker';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { InputModule } from '@beautyapp/form';
import { DatePipe, DecimalPipe } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzSpaceComponent, NzSpaceItemDirective } from 'ng-zorro-antd/space';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { SelectComponent } from '../../../../../form/src/lib/select/select.component';
import { PaymentTypePipe } from '../../../../../core/src/lib/pipes/payment-type/payment-type.pipe';

@Component({
  selector: 'beautyapp-user-transactions',
  templateUrl: './user-transactions.component.html',
  styleUrls: ['./user-transactions.component.scss'],
  imports: [
    NzDatePickerComponent,
    FormsModule,
    NzFormDirective,
    ReactiveFormsModule,
    TranslateModule,
    InputModule,
    SelectComponent,
    NzButtonComponent,
    NzTableModule,
    NzIconDirective,
    NzTooltipDirective,
    PaymentTypePipe,
    DatePipe,
    DecimalPipe,
    NzTagComponent,
    NzSpaceComponent,
    NzSpaceItemDirective,
    NzRangePickerComponent
]
})
export class UserTransactionsComponent {
  userId = input.required<string>();

  private service: OperationService = inject(OperationService);
  private translateService: TranslateService = inject(TranslateService);
  private drawerService: NzDrawerService = inject(NzDrawerService);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private employeeService: EmployeeService = inject(EmployeeService);


  trackByIndex = trackByIndex;

  sub$: Subject<any> = new Subject<any>();
  searchForm: UntypedFormGroup;

  today = format(new Date(), 'yyyy-MM-dd');
  ranges = {
    დღეს: [new Date(), new Date()],
    'მიმდინარე თვე': [startOfMonth(new Date()), endOfMonth(new Date())],
    'წინა თვე': [
      startOfMonth(subMonths(new Date(), 1)),
      endOfMonth(subMonths(new Date(), 1))
    ],
    'მიმდინარე წელი': [startOfYear(new Date()), endOfYear(new Date())]
  };
  date: any;
  dateFormat = 'dd/MM/yyyy';

  operationSource = [
    {
      id: 'BOOKING',
      name: this.translateService.instant(
        'statistic.operation.operationSource.reservation'
      )
    },
    {
      id: 'SALARY',
      name: this.translateService.instant(
        'statistic.operation.operationSource.salary'
      )
    },
    {
      id: 'REALIZATION',
      name: this.translateService.instant(
        'statistic.operation.operationSource.realization'
      )
    },
    {
      id: 'EXPENCE',
      name: this.translateService.instant(
        'statistic.operation.operationSource.expense'
      )
    },
    {
      id: 'PURCHASE',
      name: this.translateService.instant(
        'statistic.operation.operationSource.purchase'
      )
    },
    {
      id: 'BALANCE',
      name: this.translateService.instant(
        'statistic.operation.operationSource.popOpBalance'
      )
    },
    {
      id: 'BALANCERETURN',
      name: this.translateService.instant(
        'statistic.operation.operationSource.returnBalance'
      )
    },
    {
      id: 'CASHIER',
      name: this.translateService.instant(
        'statistic.operation.operationSource.cashRegister'
      )
    }
  ];

  employees = toSignal(this.employeeService.getDropdown().pipe(
    map((res) => res.map((m: any) => ({ ...m, fullName: m.user?.fullName })))
  ));

  transactionResource = rxResource({
    params: () => ({
      userId: this.userId(),
      page: this.page(),
      pageSize: this.pageSize(),
      searchValue: this.searchValue()
    }),
    stream: ({ params }) => this.service.get({
      page: params.page,
      pageSize: params.pageSize,
      search: {
        ...params.searchValue,
        userId: params.userId
      }
    })
  });

  loadData = computed(() => this.transactionResource.value()?.data || []);
  loader = computed(() => this.transactionResource.isLoading());
  total = computed(() => this.transactionResource.value()?.meta?.itemCount || 0);
  totals = computed(() => this.transactionResource.value()?.totals || {});
  pageSize = signal(10);
  page = signal(1);
  searchValue = signal({} as any);

  constructor() {
    this.searchForm = this.fb.group({
      date: { begin: startOfMonth(new Date()), end: new Date() },
      paymentType: null,
      operationId: null,
      serviceId: null,
      employeeId: null,
      userId: null,
      user: null,
      operationSource: null
    });
  }

  search() {
    if (this.searchForm.value) {
      this.searchValue.set(this.searchForm.value);
      this.page.set(1);
    }
  }

  clearSearch() {
    this.searchForm.reset();
    // The range-picker is bound to the standalone `date` model (not the form),
    // so resetting the form alone leaves the start/end date filter populated —
    // clear it explicitly (beaut-283).
    this.date = null;
    this.searchValue.set({} as any);
    this.page.set(1);
  }

  changeCalendar($event: (Date | null)[]) {

    if (!$event || !$event.length) {
      return;
    }

    const [start, end] = $event;

    this.searchForm.get('date')?.setValue({
      begin: start && format(start, 'yyyy-MM-dd 00:00:00'),
      end: end && format(end, 'yyyy-MM-dd 23:59:59')
    });
    this.searchValue.set(this.searchForm.value);
    this.page.set(1);
  }

  paginatorChange(pageEvent: any) {
    this.page.set(pageEvent.pageIndex);
    this.pageSize.set(pageEvent.pageSize);
  }

  getDetail(row: any) {
    if (row.booking && row.booking.services.length) {
      return row.booking.services.map((m: any) => m.name).join(', ');
    }

    return 'N/A';
  }

  view(event: any) {
    const title = `${event.operationSource}  # ${event.id}`;

    const drawerRef = this.drawerService.create<
      OperationDetailComponent,
      { operationId: number },
      string
    >({
      nzTitle: title,
      nzContent: OperationDetailComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: {
        padding: '0 15px 15px 15px'
      },
      nzData: {
        operationId: event.id
      }
    });
  }
}
